/**
 * useSubjectJourneyTimeline — Chronological care journey for a subject actor.
 *
 * DRR-126 Phase 6: Given a subject_actor_id (resident or any care subject),
 * returns all past + present constellation instances they were involved in,
 * grouped and sorted newest-first. Each event = one constellation instance
 * with task summary, performer list, and mandate ref.
 *
 * Powers the JourneyTimeline component — the "past / present" view of a
 * resident's full care journey across all care events.
 *
 * Data tier: T2 (operational). No clinical content — task metadata only.
 * Backend enforces scope per DRR-102 + DRR-114.
 */

import { useMemo } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

// ---------------------------------------------------------------------------
// GraphQL
// ---------------------------------------------------------------------------

const SUBJECT_JOURNEY_TIMELINE_QUERY = gql`
  query SubjectJourneyTimeline($subjectActorId: String!, $limit: Int) {
    subjectJourneyTimeline(subjectActorId: $subjectActorId, limit: $limit) {
      totalEvents
      activeEventCount
      events {
        eventId
        constellationId
        constellationName
        instanceId
        startedAt
        lastActivityAt
        status
        taskCount
        taskTypes
        performerCount
        isRegulated
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface JourneyTimelineEvent {
  eventId: string;
  constellationId: string;
  /** Human-readable constellation name from backend (e.g. "Care Service Plan") */
  constellationName: string;
  instanceId: string;
  /** ISO 8601 — when the care event started */
  startedAt: string;
  /** ISO 8601 — most recent activity in this event */
  lastActivityAt: string;
  /** in_progress | completed | overdue | pending */
  status: string;
  taskCount: number;
  taskTypes: string[];
  /** Number of staff involved — no IDs (DRR-128) */
  performerCount: number;
  /** Whether this event has a regulatory mandate — no config ID (DRR-117) */
  isRegulated: boolean;
}

interface QueryData {
  subjectJourneyTimeline: {
    totalEvents: number;
    activeEventCount: number;
    events: JourneyTimelineEvent[];
  };
}

export interface SubjectJourneyTimelineResult {
  events: JourneyTimelineEvent[];
  /** Active events (in_progress or overdue) — appear at top of timeline */
  activeEvents: JourneyTimelineEvent[];
  /** Completed/pending events — past journey */
  historicalEvents: JourneyTimelineEvent[];
  totalEvents: number;
  activeEventCount: number;
  loading: boolean;
  error?: Error;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Fetch chronological journey timeline for a subject actor.
 *
 * @param subjectActorId - Sequential actor ID of the subject (resident/patient)
 * @param limit          - Max events to return (default 50, max 200)
 * @param skip           - Skip the query
 */
export function useSubjectJourneyTimeline(
  subjectActorId: string | undefined,
  limit?: number,
  skip = false,
): SubjectJourneyTimelineResult {
  const { data, loading, error } = useQuery<QueryData>(
    SUBJECT_JOURNEY_TIMELINE_QUERY,
    {
      variables: {
        subjectActorId: subjectActorId ?? '',
        limit: limit ?? null,
      },
      skip: skip || !subjectActorId,
      fetchPolicy: 'cache-and-network',
    },
  );

  const timeline = data?.subjectJourneyTimeline;
  const events = timeline?.events ?? [];

  const { activeEvents, historicalEvents } = useMemo(() => {
    const active = events.filter(
      (e) => e.status === 'in_progress' || e.status === 'overdue',
    );
    const historical = events.filter(
      (e) => e.status !== 'in_progress' && e.status !== 'overdue',
    );
    return { activeEvents: active, historicalEvents: historical };
  }, [events]);

  return {
    events,
    activeEvents,
    historicalEvents,
    totalEvents: timeline?.totalEvents ?? 0,
    activeEventCount: timeline?.activeEventCount ?? 0,
    loading,
    error: error as Error | undefined,
  };
}
